'use client';

import { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { Media } from '../../payload-types';
import { cn } from '../../utilities/classname';
import { MediaElement } from './media-element';
import type { SubjectsById } from './subjects-by-id';
import { Portal } from './portal';

/** Max thumbnail tiles rendered into the DOM for the strip. */
export const MAX_TILES = 5;
/** Thumbnails visible at the narrowest container width (container-query base step). */
export const BASE_STEP = 3;

// Container-query reveal utilities for tiles beyond BASE_STEP: the 4th tile
// (index 3) appears at the first step up, the 5th (index 4) at the next. The
// container context is established by the `@container` class on the wrapper.
// Values are visual tuning only and are not asserted in tests.
const TILE_REVEAL: Record<number, string> = {
  3: 'hidden @[22rem]:block',
  4: 'hidden @[30rem]:block',
};

export const ReviewImageGallery: React.FC<{
  images: Media[];
  reviewerName: string;
  /** Each photo's Subject attribute by Media id, from the server parent (`subjectsById('media', images)`). */
  subjects?: SubjectsById;
}> = ({ images, reviewerName, subjects }) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [index, setIndex] = useState(0);

  if (images.length === 0) { return null; }

  const count = images.length;
  const tiles = images.slice(0, MAX_TILES);
  const showCountTile = count > BASE_STEP;

  const openAt = (i: number) => {
    setIndex(i);
    dialogRef.current?.showModal();
  };
  const go = (delta: number) => setIndex((i) => (i + delta + count) % count);

  return (
    <div className="@container mt-3">
      <ul className="flex gap-2">
        {tiles.map((image, i) => (
          <li key={image.id ?? i} className={cn('shrink-0', TILE_REVEAL[i])}>
            <button
              type="button"
              onClick={() => openAt(i)}
              className="block size-16 overflow-hidden rounded-box border border-base-300 hover:brightness-95 cursor-pointer"
              aria-label={`View photo ${i + 1} of ${count} from ${reviewerName}'s review`}
              data-testid="review-thumb"
            >
              <MediaElement data={image} className="size-full object-cover" sizes="64px" subject={subjects?.[image.id]} />
            </button>
          </li>
        ))}

        {showCountTile ? (
          <li className="shrink-0">
            <button
              type="button"
              onClick={() => openAt(0)}
              className="flex size-16 items-center justify-center rounded-box border border-base-300 bg-base-200 text-sm font-medium hover:brightness-95 cursor-pointer"
              aria-label={`View all ${count} photos from ${reviewerName}'s review`}
              data-testid="review-photo-count"
            >
              {count}
            </button>
          </li>
        ) : null}
      </ul>

      <Portal>
        <dialog
          ref={dialogRef}
          className="modal"
          aria-label={`Photos from ${reviewerName}'s review`}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') { go(-1); }
            if (e.key === 'ArrowRight') { go(1); }
          }}
        >
          <div className="modal-box max-w-3xl bg-base-100 p-0">
            <form method="dialog">
              <button className="btn btn-circle btn-ghost btn-sm absolute right-2 top-2 z-10" aria-label="Close">
                <X />
              </button>
            </form>

            <figure className="relative flex items-center justify-center bg-base-200">
              <div
                className="flex max-h-[80vh] w-full items-center justify-center"
                data-testid="review-modal-image"
              >
                <MediaElement
                  data={images[index]}
                  className="max-h-[80vh] w-auto object-contain"
                  sizes="(max-width: 768px) 100vw, 768px"
                  subject={subjects?.[images[index].id]}
                />
              </div>

              {count > 1 ? (
                <>
                  <button
                    type="button"
                    onClick={() => go(-1)}
                    className="btn btn-circle btn-sm absolute left-2 top-1/2 -translate-y-1/2"
                    aria-label="Previous image"
                  >
                    <ChevronLeft />
                  </button>
                  <button
                    type="button"
                    onClick={() => go(1)}
                    className="btn btn-circle btn-sm absolute right-2 top-1/2 -translate-y-1/2"
                    aria-label="Next image"
                  >
                    <ChevronRight />
                  </button>
                  <span
                    className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-base-content/60 px-2 py-0.5 text-xs text-base-100"
                    data-testid="review-modal-counter"
                  >
                    {index + 1} / {count}
                  </span>
                </>
              ) : null}
            </figure>
          </div>

          <form method="dialog" className="modal-backdrop">
            <button>close</button>
          </form>
        </dialog>
      </Portal>
    </div>
  );
};
